import React, { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from "react";
import { Container, Box } from "@mui/material";
import RecruiterTab from "./DashboardV2/DashboardContent/RecruiterTab";
import DashboardContent from "./DashboardV2/DashboardContent";
import CandidatesContent from "./DashboardV2/CandidatesContent";
import CVThequeContent from "./DashboardV2/CVThequeContent";
import JobsContent from "./DashboardV2/JobsContent";
import MessagesContent from "./DashboardV2/MessagesContent";
import sh from "../../common/utils/sh";
import trackEvent from "../../common/utils/trackEvent";
import engagementTracker from "../engagement/engagementTracker";
import consumer from "../channels/consumer";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const RECRUITER_TAB_KEYS = ["dashboard", "applications", "jobs", "cv_theque", "messages", "invoices"];

const Recruiter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const recruiterId = useSelector((state) => state.current_recruiter?.id);

  const [loading, setLoading] = useState(false);
  const [jobStatus, setJobStatus] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [kanbanSummary, setKanbanSummary] = useState({});
  const [selectedRecruiterTab, setSelectedRecruiterTab] = useState(() => {
    const params = new URLSearchParams(location.search || "");
    const tabKey = params.get("tab");
    const idx = RECRUITER_TAB_KEYS.indexOf(tabKey);
    return idx !== -1 ? idx : 0;
  });
  const [selectedStatsTab, setSelectedStatsTab] = useState(0);
  const [metricsData, setMetricsData] = useState({});
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsPeriod, setMetricsPeriod] = useState(null);
  const [period, setPeriod] = useState("monthly");

  const [kanban, setKanban] = useState({});
  const [kanbanColumns, setKanbanColumns] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [kanbanLoaded, setKanbanLoaded] = useState(false);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const metricsInvalidateTimeoutRef = useRef(null);

  const recruiterTabKeys = useMemo(() => RECRUITER_TAB_KEYS, []);
  const scrollYRef = useRef(0);

  const updateUrlTabs = (nextRecruiterTabIdx) => {
    const params = new URLSearchParams(window.location.search || "");
    params.set("tab", recruiterTabKeys[nextRecruiterTabIdx] || recruiterTabKeys[0]);
    params.delete("conversation");

    scrollYRef.current = window.scrollY;

    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  };

  useLayoutEffect(() => {
    window.scrollTo({
      top: scrollYRef.current,
      left: 0,
      behavior: "auto",
    });
  }, [location.search]);

  const handleRecruiterTabChange = (nextIdx) => {
    setSelectedRecruiterTab(nextIdx);
    updateUrlTabs(nextIdx);
    engagementTracker.switchTab(recruiterTabKeys[nextIdx] || recruiterTabKeys[0]);
    trackEvent("recruiter_nav", `tab_${recruiterTabKeys[nextIdx] || recruiterTabKeys[0]}`);
  };

  const invalidateMetrics = useCallback(() => {
    if (metricsInvalidateTimeoutRef.current) {
      window.clearTimeout(metricsInvalidateTimeoutRef.current);
    }

    metricsInvalidateTimeoutRef.current = window.setTimeout(() => {
      setMetricsPeriod(null);
      metricsInvalidateTimeoutRef.current = null;
    }, 500);
  }, []);

  const fetchJobStatus = () => {
    setLoading(true);
    sh.get("/recruiter/dashboard")
      .then((res) => {
        setJobStatus(res.data?.data?.job_statuses || []);
        setCandidates(res.data?.data?.suggested_candidates || []);
        setKanbanSummary(res.data?.data?.kanban_summary || {});
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchKanban = () => {
    if (kanbanLoading || kanbanLoaded) return;
    setKanbanLoading(true);

    sh.get("/recruiter/kanban")
      .then((res) => {
        const payload = res.data?.data || {};
        const kanbanData = {
          ...payload,
          closed_applications: payload?.inactive_cards || [],
        };
        setKanban(kanbanData);
        setKanbanColumns(payload?.columns || []);
        setKanbanLoaded(true);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setKanbanLoading(false);
      });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const tabKey = params.get("tab");

    if (tabKey) {
      const idx = recruiterTabKeys.indexOf(tabKey);
      if (idx !== -1 && idx !== selectedRecruiterTab) setSelectedRecruiterTab(idx);
    }
  }, [location.search, recruiterTabKeys, selectedRecruiterTab]);

  useEffect(() => {
    engagementTracker.switchTab(recruiterTabKeys[selectedRecruiterTab] || recruiterTabKeys[0]);
  }, [selectedRecruiterTab, recruiterTabKeys]);

  useEffect(() => {
    return () => engagementTracker.switchTab(null);
  }, []);

  useEffect(() => {
    return () => {
      if (metricsInvalidateTimeoutRef.current) {
        window.clearTimeout(metricsInvalidateTimeoutRef.current);
      }
    };
  }, []);


  useEffect(() => {
    fetchJobStatus();
  }, []);

  const fetchMessagesUnreadCount = useCallback(() => {
    sh.get("/recruiter/conversations/unread_count")
      .then((res) => {
        setMessagesUnreadCount(res.data?.unread_count || 0);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  useEffect(() => {
    fetchMessagesUnreadCount();
  }, [fetchMessagesUnreadCount]);

  useEffect(() => {
    if (!recruiterId) return undefined;

    const subscription = consumer.subscriptions.create(
      { channel: "RecruiterChannel", id: recruiterId },
      {
        received(data) {
          if (data?.type !== "conversation.updated") return;
          if (data.last_message?.sender_type !== "Candidate") return;
          fetchMessagesUnreadCount();
        },
      }
    );

    return () => subscription.unsubscribe();
  }, [recruiterId, fetchMessagesUnreadCount]);

  useEffect(() => {
    if (selectedRecruiterTab !== 1) return;
    fetchKanban();
  }, [selectedRecruiterTab]);

  useEffect(() => {
    if (selectedRecruiterTab !== 0) return;
    if (selectedStatsTab !== 1) return;
    if (metricsLoading) return;
    if (metricsPeriod === period) return;

    setMetricsLoading(true);
    sh.get("/recruiter/dashboard_stats", { params: { period, locale: i18nLocale } })
      .then((res) => {
        setMetricsData(res.data?.data || {});
        setMetricsPeriod(period);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setMetricsLoading(false);
      });
  }, [metricsLoading, metricsPeriod, period, selectedRecruiterTab, selectedStatsTab]);

  return (
    <Box sx={{py: 4}}>
      <Container sx={{ maxWidth: "1300px !important" }}>
        <RecruiterTab
          selectedRecruiterTab={selectedRecruiterTab}
          setSelectedRecruiterTab={handleRecruiterTabChange}
          messagesUnreadCount={messagesUnreadCount}
        />

        {selectedRecruiterTab === 0 && (
          <DashboardContent
            selectedStatsTab={selectedStatsTab}
            setSelectedStatsTab={setSelectedStatsTab}
            kanban={kanban}
            kanbanSummary={kanbanSummary}
            jobStatus={jobStatus}
            candidates={candidates}
            loading={loading}
            metricsData={metricsData}
            metricsLoading={metricsLoading}
            period={period}
            setPeriod={setPeriod}
          />
        )}
        {selectedRecruiterTab === 1 && (
          <CandidatesContent
            kanban={kanban}
            setKanban={setKanban}
            kanbanColumns={kanbanColumns}
            loading={kanbanLoading}
            onMetricsInvalidate={invalidateMetrics}
          />
        )}
        {selectedRecruiterTab === 2 && <JobsContent />}
        {selectedRecruiterTab === 3 && <CVThequeContent setKanban={setKanban} />}
        {selectedRecruiterTab === 4 && <MessagesContent onUnreadCountChange={setMessagesUnreadCount} />}
      </Container>
    </Box>
  );
};

export default Recruiter;
